export interface OptionDeliverable {
	symbol: string;
	assetType: string;
	deliverableUnits: number;
}

export interface OptionContract {
	putCall: "PUT" | "CALL";
	symbol: string;
	description: string;
	exchangeName: string;
	bid: number;
	ask: number;
	last: number;
	mark: number;
	bidSize: number;
	askSize: number;
	bidAskSize: string;
	lastSize: number;
	highPrice: number;
	lowPrice: number;
	openPrice: number;
	closePrice: number;
	totalVolume: number;
	tradeTimeInLong: number;
	quoteTimeInLong: number;
	netChange: number;
	volatility: number;
	delta: number;
	gamma: number;
	theta: number;
	vega: number;
	rho: number;
	openInterest: number;
	timeValue: number;
	theoreticalOptionValue: number;
	theoreticalVolatility: number;
	optionDeliverablesList: OptionDeliverable[];
	strikePrice: number;
	expirationDate: string;
	daysToExpiration: number;
	expirationType: string;
	lastTradingDay: number;
	multiplier: number;
	settlementType: string;
	deliverableNote: string;
	percentChange: number;
	markChange: number;
	markPercentChange: number;
	intrinsicValue: number;
	extrinsicValue: number;
	optionRoot: string;
	exerciseType: string;
	high52Week: number;
	low52Week: number;
	nonStandard: boolean;
	pennyPilot: boolean;
	inTheMoney: boolean;
	mini: boolean;
}

type StrikeMap = Record<string, OptionContract[]>;
type ExpDateMap = Record<string, StrikeMap>;

export interface OptionChain {
	symbol: string;
	status: string;
	strategy: string;
	interval: number;
	isDelayed: boolean;
	isIndex: boolean;
	interestRate: number;
	underlyingPrice: number;
	volatility: number;
	daysToExpiration: number;
	dividendYield: number;
	numberOfContracts: number;
	assetMainType: string;
	assetSubType: string;
	isChainTruncated: boolean;
	callExpDateMap: ExpDateMap;
	putExpDateMap: ExpDateMap;
}

export class OptionChainAnalyzer {
	constructor(private optionChain?: OptionChain) {}

	public getMaxPainByExpiration(): Record<string, { strike: number; pain: number }> | null {
		if (!this.optionChain || !this.optionChain.callExpDateMap || !this.optionChain.putExpDateMap) {
			return null;
		}

		const result: Record<string, { strike: number; pain: number }> = {};

		const allExpirations = Object.keys(this.optionChain.callExpDateMap)
			.map(exp => exp.split(":")[0])
			.filter(dateStr => !isNaN(Date.parse(dateStr)))
			.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

		for (const exp of allExpirations) {
			const fullKey = Object.keys(this.optionChain.callExpDateMap).find(k => k.startsWith(exp));
			if (!fullKey) continue;

			const callStrikes = this.optionChain.callExpDateMap[fullKey];
			const putStrikes = this.optionChain.putExpDateMap[fullKey] || {};

			const allStrikes = Array.from(
				new Set([
					...Object.keys(callStrikes),
					...Object.keys(putStrikes),
				].map(strike => parseFloat(strike)))
			).sort((a, b) => a - b);

			let minPain = Infinity;
			let maxPainStrike = allStrikes[0];

			for (const testStrike of allStrikes) {
				let totalPain = 0;

				// CALLS: If test strike < call strike → loss
				for (const [callStrikeStr, contracts] of Object.entries(callStrikes)) {
					const strike = parseFloat(callStrikeStr);
					const callOI = contracts.reduce((sum, c) => sum + (c.openInterest || 0), 0);
					if (testStrike < strike) {
						totalPain += callOI * (strike - testStrike);
					}
				}

				// PUTS: If test strike > put strike → loss
				for (const [putStrikeStr, contracts] of Object.entries(putStrikes)) {
					const strike = parseFloat(putStrikeStr);
					const putOI = contracts.reduce((sum, p) => sum + (p.openInterest || 0), 0);
					if (testStrike > strike) {
						totalPain += putOI * (testStrike - strike);
					}
				}

				if (totalPain < minPain) {
					minPain = totalPain;
					maxPainStrike = testStrike;
				}
			}

			result[exp] = { strike: maxPainStrike, pain: minPain };
		}

		return result;
	}
}




